// ============================================
// FILE: /frontend/src/components/dashboard/PromoteListingModal.jsx
// UPDATED: Now navigates to checkout page instead of handling payment
// ============================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Star, Zap, Loader2, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { dashboardAPI } from '../../api/dashboard.api';

export default function PromoteListingModal({ isOpen, onClose, listing, onSuccess }) {
  console.log('[PromoteListingModal] 🎨 Rendering:', { isOpen, listing });

  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);

  useEffect(() => {
    if (isOpen && listing) {
      fetchPricing();
    }
  }, [isOpen, listing]);

  const fetchPricing = async () => {
    console.log('[PromoteListingModal] 📡 Fetching pricing for listing:', listing.id);
    setFetchLoading(true);
    try {
      const response = await dashboardAPI.getFeaturedPricing(listing.id);
      console.log('[PromoteListingModal] ✅ Pricing received:', response);
      
      setPlans(response.plans || []);
      setDaysRemaining(response.daysRemaining);

      if (response.plans?.length === 0) {
        toast.error('No promotion plans available for this listing');
      }
    } catch (error) {
      console.error('[PromoteListingModal] ❌ Error:', error);
      toast.error('Failed to load pricing plans');
    } finally {
      setFetchLoading(false);
    }
  };

  // ⭐ UPDATED: Navigate to checkout instead of processing payment
  const handlePromote = () => {
    if (!selectedPlan) {
      toast.error('Please select a promotion plan');
      return;
    }

    console.log('[PromoteListingModal] 🚀 Navigating to checkout:', { 
      listing: listing.id, 
      plan: selectedPlan
    });

    // Navigate to checkout page with listing and plan data
    navigate('/promote/checkout', {
      state: {
        listing: listing,
        plan: selectedPlan
      }
    });

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Star className="w-6 h-6 text-yellow-500" />
              Promote Listing
            </h2>
            <p className="text-sm text-muted-foreground mt-1">{listing?.title}</p>
            {daysRemaining !== null && (
              <p className="text-xs text-muted-foreground mt-1">
                📅 Listing expires in {daysRemaining} days
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-md transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {fetchLoading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading plans...</p>
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-2">No promotion plans available</p>
              <p className="text-xs text-muted-foreground">
                This listing may have expired or be ineligible for promotion
              </p>
            </div>
          ) : (
            <>
              {/* Benefits */}
              <div className="mb-6">
                <h3 className="font-semibold mb-2">Why promote?</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    Appear at the top of search results
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    Get highlighted with a featured badge
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    Increase visibility by up to 10x
                  </li>
                </ul>
              </div>

              {/* Plan Selection */}
              <h3 className="font-semibold mb-4">Select Duration</h3>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {plans.map((plan) => (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan)}
                    className={`p-4 border-2 rounded-lg transition relative ${
                      selectedPlan?.id === plan.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-gray-200 hover:border-primary/50'
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        Popular
                      </div>
                    )}
                    <div className="text-center">
                      <p className="text-2xl font-bold mb-1">{plan.label}</p>
                      <p className="text-3xl font-bold text-primary mb-1">
                        KES {plan.price.toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        ~KES {Math.round(plan.price / plan.duration)}/day
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button 
                  onClick={onClose} 
                  className="flex-1 px-4 py-3 border rounded-md hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePromote}
                  disabled={!selectedPlan}
                  className="flex-1 px-4 py-3 bg-primary text-white rounded-md hover:bg-primary/90 transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Star className="w-4 h-4" />
                  Continue to Checkout
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}